// Preview for a tabular (CSV/XLSX) lake import that was scoped to a subset of
// rows/columns. The selection (`selected_columns` = Parquet column names,
// `selected_rows` = Parquet `__row_index` values) was built against the lake
// document's Parquet representation — NOT the raw uploaded bytes — so the
// preview must read that same Parquet to line up. We fetch it via
// `getTabularPreview` (the exact source the selection was built from) and clip
// client-side with the same semantics the server-side `clip_tabular` uses for
// extraction, so the preview shows precisely what extraction will see.

import { Loader2 } from 'lucide-react'
import { useMemo } from 'react'
import { useTabularScopePreview } from '@/features/documents/hooks/useTabularScopePreview'
import type { TabularColumn } from '@/services/api/lake'

const ROW_INDEX_COL = '__row_index'

// Cap rendered rows so an accidental large selection can't blow up the DOM.
const MAX_RENDER_ROWS = 1_000

type Cell = string | number | boolean | null

interface ClippedTable {
  header: string[]
  body: Cell[][]
}

/**
 * Clip the Parquet preview (columns + rows) to the import selection.
 *
 * Columns are kept in the Parquet's natural order, filtered to the selected
 * names (mirrors `clip_tabular`). Rows are kept when their `__row_index` value
 * is in `selectedRows`. A null/empty selection keeps everything.
 */
function clipTabular(
  columns: TabularColumn[],
  rows: Cell[][],
  selectedColumns: string[] | null,
  selectedRows: number[] | null
): ClippedTable {
  const rowIndexPos = columns.findIndex(c => c.name === ROW_INDEX_COL)
  const dataCols = columns
    .map((c, pos) => ({ name: c.name, pos }))
    .filter(c => c.name !== ROW_INDEX_COL)

  const wanted = selectedColumns?.length ? new Set(selectedColumns) : null
  const keepCols = wanted ? dataCols.filter(c => wanted.has(c.name)) : dataCols

  const rowSet = selectedRows?.length ? new Set(selectedRows) : null
  const keptRows =
    rowSet && rowIndexPos >= 0 ? rows.filter(r => rowSet.has(Number(r[rowIndexPos]))) : rows

  return {
    header: keepCols.map(c => c.name),
    body: keptRows.map(r => keepCols.map(c => r[c.pos] ?? '')),
  }
}

export function TabularScopePreview({
  lakeDocumentId,
  selectedColumns,
  selectedRows,
}: {
  lakeDocumentId: string
  selectedColumns: string[] | null
  selectedRows: number[] | null
}) {
  const { data, loading, error } = useTabularScopePreview(lakeDocumentId, selectedRows)

  // Serialize the selection so the clip memo only re-runs on value changes.
  const selColsKey = JSON.stringify(selectedColumns ?? null)
  const selRowsKey = JSON.stringify(selectedRows ?? null)

  const table = useMemo(() => {
    if (!data) return null
    const cols = JSON.parse(selColsKey) as string[] | null
    const rowsSel = JSON.parse(selRowsKey) as number[] | null
    return clipTabular(data.columns, data.rows, cols, rowsSel)
  }, [data, selColsKey, selRowsKey])

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading selection…</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      </div>
    )
  }

  if (!table || table.header.length === 0) {
    return (
      <div className="p-4">
        <div className="border border-border rounded p-6 text-center text-muted-foreground text-sm">
          <p>No columns in the imported selection.</p>
        </div>
      </div>
    )
  }

  const rowsTruncated = table.body.length > MAX_RENDER_ROWS
  const renderRows = rowsTruncated ? table.body.slice(0, MAX_RENDER_ROWS) : table.body

  return (
    <div className="p-4 h-full flex flex-col">
      <div className="text-xs text-muted-foreground px-3 py-1.5 border border-b-0 border-border rounded-t bg-muted/20 shrink-0">
        Showing the imported selection · {table.body.length} row
        {table.body.length === 1 ? '' : 's'} × {table.header.length} column
        {table.header.length === 1 ? '' : 's'}
      </div>
      <div className="border border-border rounded-b overflow-auto flex-1">
        <table className="w-full text-xs border-collapse">
          <tbody>
            <tr className="bg-muted/50 font-medium sticky top-0">
              {table.header.map(name => (
                <td
                  key={`h-${name}`}
                  className="border-b border-r border-border px-2 py-1 text-foreground align-top max-w-[240px] truncate"
                  title={name}
                >
                  {name}
                </td>
              ))}
            </tr>
            {renderRows.map((row, i) => (
              <tr key={`r-${i}`}>
                {row.map((cell, j) => {
                  const text = cell == null ? '' : String(cell)
                  return (
                    <td
                      key={`r-${i}-c-${j}`}
                      className="border-b border-r border-border px-2 py-1 text-foreground align-top max-w-[240px] truncate"
                      title={text}
                    >
                      {text}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rowsTruncated && (
        <div className="text-xs text-muted-foreground text-center py-2 italic border border-t-0 border-border bg-muted/40 shrink-0">
          Showing first {MAX_RENDER_ROWS.toLocaleString()} of {table.body.length.toLocaleString()}{' '}
          selected rows
        </div>
      )}
    </div>
  )
}
